<template>
  <div class="technician-report-page">
    <header-portrate />
    <exports-print ref="exportbar" />
    <TabsComp :tabs="tabs" />

    <div class="report-shell" :style="`direction:${lang.dir || 'ltr'}`">
      <div class="report-toolbar">
        <h5 class="page-title">{{ t('technician_analysis', 'Technician Analysis') }}</h5>
        <div class="filters">
          <b-form-input v-model="filters.sdate" type="date" />
          <b-form-input v-model="filters.edate" type="date" />
          <b-form-select v-model="filters.empid" :options="employeeOptions" />
          <b-form-select v-model="filters.status" :options="statusOptions" />
          <b-button size="sm" variant="primary" @click="loadReport" :disabled="loading">
            <v-icon small class="mr-1">mdi-magnify</v-icon>
            {{ t('search', 'Search') }}
          </b-button>
        </div>
      </div>

      <div class="analysis-grid">
        <div class="analysis-cell">
          <span>{{ t('total', 'Total') }}</span>
          <strong>{{ analysis.total || 0 }}</strong>
        </div>
        <div class="analysis-cell">
          <span>{{ t('open', 'Open') }}</span>
          <strong>{{ analysis.open || 0 }}</strong>
        </div>
        <div class="analysis-cell paused">
          <span>{{ t('paused', 'Paused') }}</span>
          <strong>{{ analysis.paused || 0 }}</strong>
        </div>
        <div class="analysis-cell fixed">
          <span>{{ t('fixed', 'Fixed') }}</span>
          <strong>{{ analysis.fixed || 0 }}</strong>
        </div>
      </div>

      <div class="report-grid">
        <section class="report-panel">
          <div class="section-title">{{ t('pause_causes', 'Pause Causes') }}</div>
          <v-simple-table dense>
            <thead>
              <tr>
                <th>{{ t('cause', 'Cause') }}</th>
                <th class="text-center">{{ t('total', 'Total') }}</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="cause in analysis.by_cause || []" :key="`${cause.cause_key}-${cause.cause_label}`">
                <td>{{ cause.cause_label || cause.cause_key || '-' }}</td>
                <td class="text-center">{{ cause.total }}</td>
              </tr>
              <tr v-if="!(analysis.by_cause && analysis.by_cause.length)">
                <td colspan="2" class="text-center">{{ t('no_data', 'No data') }}</td>
              </tr>
            </tbody>
          </v-simple-table>
        </section>

        <section class="report-panel">
          <div class="section-title">{{ t('worker', 'Technician') }}</div>
          <v-simple-table dense>
            <thead>
              <tr>
                <th>{{ t('worker', 'Technician') }}</th>
                <th class="text-center">{{ t('open', 'Open') }}</th>
                <th class="text-center">{{ t('paused', 'Paused') }}</th>
                <th class="text-center">{{ t('fixed', 'Fixed') }}</th>
                <th class="text-center">{{ t('total', 'Total') }}</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="employee in analysis.by_technician || []" :key="employee.employee_id">
                <td>{{ employee.employee_name }}</td>
                <td class="text-center">{{ employee.open }}</td>
                <td class="text-center">{{ employee.paused }}</td>
                <td class="text-center">{{ employee.fixed }}</td>
                <td class="text-center">{{ employee.total }}</td>
              </tr>
              <tr v-if="!(analysis.by_technician && analysis.by_technician.length)">
                <td colspan="5" class="text-center">{{ t('no_data', 'No data') }}</td>
              </tr>
            </tbody>
          </v-simple-table>
        </section>
      </div>

      <section class="report-panel list-panel">
        <div class="section-title">{{ t('job_card_items', 'Job Card Items') }}</div>
        <v-simple-table dense>
          <thead>
            <tr>
              <th class="text-center">{{ t('cardid', 'Job Card #') }}</th>
              <th>{{ t('customer', 'Customer') }}</th>
              <th>{{ t('worker', 'Technician') }}</th>
              <th>{{ t('service', 'Service') }}</th>
              <th>{{ t('device', 'Device') }}</th>
              <th class="text-center">{{ t('status', 'Status') }}</th>
              <th>{{ t('cause', 'Cause') }}</th>
              <th>{{ t('notes', 'Notes') }}</th>
              <th class="text-center">{{ t('date', 'Date') }}</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="row in rows" :key="row.row_id">
              <td class="text-center">#{{ row.job_card_number || row.job_card_id }}</td>
              <td>{{ row.customer_name || '-' }}</td>
              <td>{{ row.technician_names || '-' }}</td>
              <td>{{ row.item_name || '-' }}</td>
              <td>{{ row.device_summary || row.plate_number || '-' }}</td>
              <td class="text-center">
                <span :class="['status-pill', row.status_key]">{{ row.status_label }}</span>
              </td>
              <td>{{ row.last_cause_label || '-' }}</td>
              <td>{{ row.last_note || '-' }}</td>
              <td class="text-center">{{ formatDate(row.last_action_date || row.received_date || row.row_created_date) }}</td>
            </tr>
            <tr v-if="!rows.length">
              <td colspan="9" class="text-center">{{ t('no_data', 'No data') }}</td>
            </tr>
          </tbody>
        </v-simple-table>
      </section>
    </div>

    <Footer />
  </div>
</template>

<script>
/* eslint-disable @typescript-eslint/explicit-module-boundary-types */
import HeaderPortrate from '@/components/Header-Portrate.vue'
import Footer from '@/components/Footer.vue'
import ExportsPrint from '@/components/exports-print.vue'
import TabsComp from '@/components/tabsComp.vue'
import { getVisibleTabsForRoute } from '@/menu/menuService'

function isoDate(date) {
  const copy = new Date(date)
  copy.setMinutes(copy.getMinutes() - copy.getTimezoneOffset())
  return copy.toISOString().slice(0, 10)
}

export default {
  components: { HeaderPortrate, Footer, ExportsPrint, TabsComp },
  data() {
    const now = new Date()
    const start = new Date(now.getFullYear(), now.getMonth(), 1)
    return {
      loading: false,
      rows: [],
      employees: [],
      analysis: {
        total: 0,
        open: 0,
        paused: 0,
        fixed: 0,
        by_cause: [],
        by_technician: [],
      },
      filters: {
        sdate: isoDate(start),
        edate: isoDate(now),
        empid: 0,
        status: 'all',
      },
    }
  },
  computed: {
    lang() {
      if (this.$cookies.get(this.$store.state.LangCooki) != null) {
        if (this.$cookies.get(this.$store.state.LangCooki) == 'en') return this.$store.state.lang.en
        if (this.$cookies.get(this.$store.state.LangCooki) == 'ur') return this.$store.state.lang.ur
        return this.$store.state.lang.ar
      }
      if (this.$store.state.deflang == 'en') return this.$store.state.lang.en
      if (this.$store.state.deflang == 'ur') return this.$store.state.lang.ur
      return this.$store.state.lang.ar
    },
    tabs() {
      return getVisibleTabsForRoute(this.$store.getters['state'], this.lang, this.$route.path)
    },
    employeeOptions() {
      const all = [{ text: this.t('all', 'All'), value: 0 }]
      return all.concat(this.employees.map((employee) => ({
        text: this.lang.langname == 'ar'
          ? (employee.employee_name || employee.employee_name_en || `#${employee.id}`)
          : (employee.employee_name_en || employee.employee_name || `#${employee.id}`),
        value: employee.id,
      })))
    },
    statusOptions() {
      return [
        { text: this.t('all', 'All'), value: 'all' },
        { text: this.t('open', 'Open'), value: 'open' },
        { text: this.t('paused', 'Paused'), value: 'paused' },
        { text: this.t('fixed', 'Fixed'), value: 'fixed' },
      ]
    },
  },
  created() {
    this.loadReport()
  },
  methods: {
    t(key, fallback) {
      return (this.lang && this.lang[key]) || fallback
    },
    async postApi(type, payload = {}) {
      const post = new FormData()
      post.append('defBranch', localStorage.getItem('defBranch') || '1')
      post.append('financeYear', localStorage.getItem('financeYear') || '')
      post.append('type', type)
      post.append('auth', this.$cookies.get(this.$store.state.COOKIEPhase))
      Object.keys(payload).forEach((key) => {
        post.append(`data[${key}]`, payload[key])
      })
      const response = await fetch(this.$store.state.SAMCOTEC.r_path, { method: 'POST', body: post })
      return response.json()
    },
    async loadReport() {
      this.loading = true
      try {
        const res = await this.postApi('getTechnicianWorkReport', this.filters)
        if (res && res.error && res.error.number === 200) {
          this.rows = res.results.data || []
          this.analysis = res.results.analysis || this.analysis
          this.employees = res.results.employees || []
        } else if (res && res.error) {
          window.alert(res.error.message || 'Error')
        }
      } finally {
        this.loading = false
      }
    },
    formatDate(value) {
      if (!value) return '-'
      return String(value).slice(0, 16)
    },
  },
}
</script>

<style scoped>
.report-shell {
  padding: 14px 18px 24px;
  min-height: calc(100vh - 140px);
  background: #f5f6f8;
}
.report-toolbar,
.analysis-grid,
.report-grid,
.list-panel {
  max-width: 1440px;
  margin-left: auto;
  margin-right: auto;
}
.report-toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 12px;
}
.page-title {
  margin: 0;
  font-weight: 700;
}
.filters {
  display: grid;
  grid-template-columns: 150px 150px 220px 140px auto;
  gap: 8px;
  align-items: center;
}
.analysis-grid {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 8px;
  margin-bottom: 12px;
}
.analysis-cell,
.report-panel {
  background: #fff;
  border: 1px solid #d8dde6;
  border-radius: 6px;
}
.analysis-cell {
  padding: 10px 12px;
  display: flex;
  align-items: center;
  justify-content: space-between;
}
.analysis-cell span {
  color: #667085;
  font-size: 12px;
}
.analysis-cell strong {
  font-size: 20px;
}
.analysis-cell.paused strong {
  color: #9a5b00;
}
.analysis-cell.fixed strong {
  color: #087443;
}
.report-grid {
  display: grid;
  grid-template-columns: 40% minmax(0, 1fr);
  gap: 12px;
  margin-bottom: 12px;
}
.report-panel {
  padding: 12px;
  overflow: auto;
}
.section-title {
  font-size: 13px;
  font-weight: 700;
  margin-bottom: 8px;
  color: #2f3a4a;
}
.status-pill {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 58px;
  border-radius: 999px;
  padding: 2px 8px;
  font-size: 11px;
  font-weight: 700;
  background: #e8edf3;
  color: #293241;
}
.status-pill.paused {
  background: #fff3cd;
  color: #7a4b00;
}
.status-pill.fixed {
  background: #dff6e9;
  color: #075b34;
}
@media (max-width: 1040px) {
  .report-toolbar,
  .filters {
    display: grid;
    grid-template-columns: 1fr;
    align-items: stretch;
  }
  .analysis-grid,
  .report-grid {
    grid-template-columns: 1fr;
  }
}
</style>
